<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import WootSnackbar from './Snackbar.vue';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  duration: {
    type: Number,
    default: 5000,
  },
});

const { t } = useI18n();

let toastKey = 0;
const snackMessages = ref([]);
const snackbarContainer = ref(null);

const showPopover = () => {
  try {
    const el = snackbarContainer.value;
    if (el?.matches(':popover-open')) {
      el.hidePopover();
    }
    el?.showPopover();
  } catch (e) {
    // ignore
  }
};

/**
 * Removes a single toast from the stack.
 * @param {number} key - Toast key generated when the toast was queued.
 */
const dismissToast = key => {
  snackMessages.value = snackMessages.value.filter(
    message => message.key !== key
  );
};

/**
 * Queues a toast event emitted by the global alert composable.
 * @param {Object} toast - Toast event payload.
 * @param {string} toast.message - Toast message or translation key.
 * @param {string} toast.variant - Toast visual variant.
 * @param {Object|null} toast.action - Optional toast action.
 */
const onNewToastMessage = ({
  message: originalMessage,
  variant = 'info',
  action,
}) => {
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  const message = action?.usei18n ? t(originalMessage) : originalMessage;
  const duration = action?.duration || props.duration;
  toastKey += 1;
  const key = toastKey;

  snackMessages.value.push({
    key,
    message,
    variant,
    action,
  });

  nextTick(showPopover);

  setTimeout(() => {
    dismissToast(key);
  }, duration);
};

onMounted(() => {
  emitter.on('newToastMessage', onNewToastMessage);
});

onUnmounted(() => {
  emitter.off('newToastMessage', onNewToastMessage);
});
</script>

<template>
  <div
    ref="snackbarContainer"
    popover="manual"
    class="fixed top-4 left-1/2 -translate-x-1/2 max-w-[28rem] w-[calc(100%-2rem)] text-center bg-transparent border-0 p-0 m-0 outline-none overflow-visible"
  >
    <transition-group name="toast-fade" tag="div">
      <WootSnackbar
        v-for="snackMessage in snackMessages"
        :key="snackMessage.key"
        :message="snackMessage.message"
        :variant="snackMessage.variant"
        :action="snackMessage.action"
        :dismiss-label="t('COMPONENTS.SNACKBAR.DISMISS')"
        @dismiss="dismissToast(snackMessage.key)"
      />
    </transition-group>
  </div>
</template>
